"use client";

import { usePathname } from "next/navigation";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { APP_ICON_VIEW_BOX, brandAppIconElements } from "../brand-mark";
import { type PwaApp, pwaAppForPath } from "./apps";
import { INITIAL_STATE, InstallController, type InstallState } from "./install";

type InstallContext = InstallState & {
  busy: boolean;
  install: () => void;
  dismiss: () => void;
};
const Context = createContext<InstallContext | null>(null);

export function PwaInstallProvider({
  children,
  defaultApp = "player",
}: {
  children: ReactNode;
  defaultApp?: PwaApp;
}) {
  const app = pwaAppForPath(usePathname() ?? "/", defaultApp);
  const controller = useRef<InstallController | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const [state, setState] = useState(INITIAL_STATE);
  const [busy, setBusy] = useState(false);
  const [popup, setPopup] = useState<"instructions" | "dismissed" | null>(null);
  const [saved, setSaved] = useState(true);
  const [appleMobile, setAppleMobile] = useState(false);

  useEffect(() => {
    // A prompt belongs to the manifest that produced it. Preserve it within
    // one app, but discard prompts/observations when crossing portal boundaries.
    setState(INITIAL_STATE);
    setPopup(null);
    const current = new InstallController(window, Date.now, app);
    controller.current = current;
    const unsubscribe = current.subscribe(() =>
      setState(current.getSnapshot()),
    );
    current.start();
    setAppleMobile(
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1),
    );
    return () => {
      unsubscribe();
      current.stop();
      controller.current = null;
    };
  }, [app]);

  useEffect(() => {
    if (popup) dialog.current?.showModal();
    else dialog.current?.close();
  }, [popup]);

  async function install() {
    if (busy || state.installed) return;
    setBusy(true);
    const outcome = await controller.current?.install();
    setBusy(false);
    if (outcome === "unavailable") setPopup("instructions");
  }

  function dismiss() {
    setSaved(controller.current?.dismiss() ?? false);
    setPopup("dismissed");
  }

  return (
    <Context.Provider value={{ ...state, busy, install, dismiss }}>
      {children}
      <dialog
        ref={dialog}
        className="pwa-dialog"
        aria-labelledby="pwa-dialog-title"
        aria-describedby="pwa-dialog-description"
        onClose={() => setPopup(null)}
      >
        {popup === "dismissed" ? (
          <>
            <h2 id="pwa-dialog-title">
              {saved
                ? "90日間、インストールの案内を表示しません"
                : "案内を非表示にしました"}
            </h2>
            <p id="pwa-dialog-description">
              {saved
                ? "このブラウザでは、ホームのインストール案内を90日間表示しません。"
                : "このブラウザでは設定を保存できないため、このページを開いている間だけ案内を非表示にします。"}
              インストールしたくなったら、プロフィールの「アプリをインストール」からいつでも操作できます。
            </p>
            <a className="text-link" href="/account#app-install">
              プロフィールへ →
            </a>
          </>
        ) : (
          <>
            <h2 id="pwa-dialog-title">アプリをインストール</h2>
            <p id="pwa-dialog-description">
              ホーム画面やデスクトップに追加すると、アイコンからすぐにタマリバを開けます。
            </p>
            {appleMobile ? (
              <ol>
                <li>このページをSafariで開きます。</li>
                <li>ブラウザの共有メニューを開きます。</li>
                <li>「ホーム画面に追加」を選びます。</li>
                <li>
                  「Webアプリとして開く」が表示される場合はオンにして、「追加」を押します。
                </li>
              </ol>
            ) : (
              <>
                <p>
                  Chrome・Edgeでは、アドレスバーのインストールアイコン、またはブラウザのメニューから「アプリをインストール」「ホーム画面に追加」を選んでください。
                </p>
                <p>MacのSafariでは「ファイル」→「Dockに追加」を選びます。</p>
                <p className="pwa-note">
                  項目が見つからない場合は、Chrome・Edge・Safariで開き直してください。インストールの対応状況や項目名は端末・ブラウザによって異なります。
                </p>
              </>
            )}
          </>
        )}
        <form method="dialog" className="pwa-dialog-actions">
          <button className="primary-button" type="submit">
            わかりました
          </button>
        </form>
      </dialog>
    </Context.Provider>
  );
}

function useInstall() {
  const context = useContext(Context);
  if (!context) throw new Error("PwaInstallProvider is required");
  return context;
}

function AppIcon() {
  return (
    <svg
      className="pwa-app-icon"
      viewBox={APP_ICON_VIEW_BOX}
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      {brandAppIconElements(true)}
    </svg>
  );
}

function InstallPromotion({ profile = false }: { profile?: boolean }) {
  const { ready, installed, hiddenUntil, busy, install, dismiss } =
    useInstall();
  if (!ready || installed || (!profile && hiddenUntil > 0)) return null;
  const titleId = profile ? "pwa-profile-title" : "pwa-install-title";
  return (
    <section
      id={profile ? "app-install" : undefined}
      className={profile ? "pwa-banner pwa-profile" : "pwa-banner"}
      aria-labelledby={titleId}
    >
      {!profile && (
        <button
          className="pwa-close"
          type="button"
          aria-label="インストール案内を閉じる（90日間非表示）"
          aria-haspopup="dialog"
          onClick={dismiss}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="m6 6 12 12M18 6 6 18"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
      <div className="pwa-banner-copy">
        <div className="pwa-banner-intro">
          <AppIcon />
          <p className="pwa-label">練習への近道を、ホーム画面に。</p>
        </div>
        <h2 id={titleId}>アプリをインストール</h2>
        <p className="pwa-lead">
          ホーム画面からワンタップで、いつもの練習へ。スコアの記録も、配置図の振り返りも、思い立ったそのときに。
        </p>
        <div className="pwa-banner-actions">
          <button
            className="primary-button pwa-install-button"
            type="button"
            disabled={busy}
            onClick={install}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M12 3v12m-5-5 5 5 5-5M5 16v4h14v-4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {busy ? "確認中…" : "アプリをインストール"}
          </button>
        </div>
        {profile && (
          <p className="pwa-profile-note">
            ホームの案内を非表示にしていても、いつでもここからインストールできます。
          </p>
        )}
      </div>
      <div className="pwa-art" aria-hidden="true">
        <div className="pwa-phone">
          <span className="pwa-phone-camera" />
          <AppIcon />
          <span className="pwa-phone-label">タマリバ</span>
          <span className="pwa-phone-dock">
            <i />
            <i />
            <i />
          </span>
        </div>
        <span className="pwa-art-caption">タップして、次の一球へ。</span>
      </div>
    </section>
  );
}

export function InstallBanner() {
  return <InstallPromotion />;
}

export function ProfileInstall() {
  return <InstallPromotion profile />;
}
